import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Database,
  FileSearch,
  HardDrive,
  LockKeyhole,
  MailCheck,
  Pencil,
  Search,
  ShieldCheck,
  UserCheck,
  UsersRound,
  X,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { Button, EmptyState, Field, Input, LoadingLine, PageHeader, Tag } from "@/components/primitives";
import { apiFetch } from "@/lib/api";

interface Pagination { page: number; pageSize: number; total: number }

function formatDate(value: string | Date | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Shanghai" }).format(new Date(value));
}

function PaginationBar({ pagination, onPage }: { pagination: Pagination; onPage: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
  return (
    <div className="admin-pagination">
      <span>共 {pagination.total} 条 · 第 {pagination.page}/{pages} 页</span>
      <div>
        <Button size="sm" type="button" aria-label="上一页" title="上一页" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}><ChevronLeft /></Button>
        <Button size="sm" type="button" aria-label="下一页" title="下一页" disabled={pagination.page >= pages} onClick={() => onPage(pagination.page + 1)}><ChevronRight /></Button>
      </div>
    </div>
  );
}

export function AdminOverviewPage() {
  const query = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => apiFetch<{
      metrics: { users: number; activeUsers7d: number; wordBooks: number; examPapers: number; queuedJobs: number; failedJobs: number };
      services: {
        database: { ok: boolean; version: string };
        email: { configured: boolean };
        ai: { configured: boolean };
        storage: { writable: boolean; resolvedDirectory: string; freeBytes: number | null; totalBytes: number | null };
      };
      recentAudit: Array<{ id: number; action: string; targetType: string; createdAt: string; actorEmail: string | null }>;
      timestamp: string;
    }>("/admin/overview"),
    refetchInterval: 30_000,
  });
  const data = query.data;
  const freeGb = data?.services.storage.freeBytes === null || data?.services.storage.freeBytes === undefined
    ? "未知"
    : `${(data.services.storage.freeBytes / 1024 ** 3).toFixed(1)} GB`;
  return (
    <div className="admin-page">
      <PageHeader title="管理概览" description="用户、内容和运行服务的实时概况。" action={<span className="admin-updated"><Clock3 /> {data ? formatDate(data.timestamp) : "读取中"}</span>} />
      {query.isLoading ? <LoadingLine /> : null}
      {query.isError ? <div className="admin-alert admin-alert--danger"><AlertTriangle /> 无法读取管理概览，请检查 API 与数据库连接。</div> : null}
      <section className="admin-metrics" aria-label="关键指标">
        <article><UsersRound /><strong>{data?.metrics.users ?? "—"}</strong><span>全部用户</span></article>
        <article><UserCheck /><strong>{data?.metrics.activeUsers7d ?? "—"}</strong><span>7 日活跃</span></article>
        <article><BookOpen /><strong>{data ? data.metrics.wordBooks + data.metrics.examPapers : "—"}</strong><span>已发布内容</span></article>
        <article><Activity /><strong>{data?.metrics.queuedJobs ?? "—"}</strong><span>排队任务</span></article>
      </section>
      <div className="admin-overview-grid">
        <section className="admin-panel" aria-labelledby="service-health-title">
          <header className="admin-panel__header"><div><p className="section-label">运行状态</p><h2 id="service-health-title">核心服务</h2></div><Tag tone={data ? "success" : "neutral"}>{data ? "在线" : "检查中"}</Tag></header>
          <div className="service-list">
            <div><Database /><span><strong>MySQL</strong><small>{data?.services.database.version || "等待检查"}</small></span><CheckCircle2 className={data?.services.database.ok ? "status-ok" : "status-muted"} /></div>
            <div><MailCheck /><span><strong>邮件服务</strong><small>{data?.services.email.configured ? "SMTP 已配置" : "需要配置"}</small></span><span className={data?.services.email.configured ? "status-dot status-dot--ok" : "status-dot"} /></div>
            <div><ShieldCheck /><span><strong>MiMo AI</strong><small>{data?.services.ai.configured ? "密钥已配置" : "需要配置"}</small></span><span className={data?.services.ai.configured ? "status-dot status-dot--ok" : "status-dot"} /></div>
            <div><HardDrive /><span><strong>本地存储</strong><small title={data?.services.storage.resolvedDirectory}>{data?.services.storage.writable ? `可写 · 剩余 ${freeGb}` : "目录不可写"}</small></span><span className={data?.services.storage.writable ? "status-dot status-dot--ok" : "status-dot status-dot--danger"} /></div>
          </div>
        </section>
        <section className="admin-panel" aria-labelledby="recent-audit-title">
          <header className="admin-panel__header"><div><p className="section-label">最近活动</p><h2 id="recent-audit-title">管理操作</h2></div></header>
          <div className="audit-compact">
            {data?.recentAudit.map((item) => <div key={item.id}><span className="audit-mark" /><span><strong>{item.action}</strong><small>{item.actorEmail ?? "系统"} · {formatDate(item.createdAt)}</small></span></div>)}
            {data && data.recentAudit.length === 0 ? <p className="admin-muted">暂无审计记录。</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
}

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  status: "active" | "locked" | "disabled";
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  roles: string[];
}

type PendingUserAction = { user: AdminUser; body?: { status?: AdminUser["status"]; admin?: boolean }; revoke?: boolean; title: string; description: string };

export function AdminUsersPage() {
  const client = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [verified, setVerified] = useState("");
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PendingUserAction | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ displayName: "", email: "", emailVerified: false });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const params = new URLSearchParams({ page: String(page), pageSize: "25" });
  if (q) params.set("q", q);
  if (status) params.set("status", status);
  if (role) params.set("role", role);
  if (verified) params.set("verified", verified);
  const query = useQuery({
    queryKey: ["admin", "users", q, status, role, verified, page],
    queryFn: () => apiFetch<{ users: AdminUser[]; pagination: Pagination; currentUserId: string }>(`/admin/users?${params}`),
  });
  const confirmAction = async () => {
    if (!pending) return;
    setBusy(true);
    setNotice("");
    try {
      await apiFetch(`/admin/users/${pending.user.id}${pending.revoke ? "/revoke-sessions" : ""}`, {
        method: pending.revoke ? "POST" : "PATCH",
        ...(pending.body ? { body: JSON.stringify(pending.body) } : {}),
      });
      setNotice("操作已完成并写入审计日志。");
      setPending(null);
      await Promise.all([client.invalidateQueries({ queryKey: ["admin", "users"] }), client.invalidateQueries({ queryKey: ["admin", "overview"] })]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "request_failed";
      const labels: Record<string, string> = { cannot_disable_self: "不能停用当前账号。", cannot_remove_own_admin: "不能移除自己的管理员权限。", last_admin_protected: "必须至少保留一个可用管理员。" };
      setNotice(labels[message] ?? "操作失败，请稍后重试。");
      setPending(null);
    } finally { setBusy(false); }
  };
  const openEditor = (user: AdminUser) => {
    setEditing(user);
    setEditForm({ displayName: user.displayName ?? "", email: user.email, emailVerified: Boolean(user.emailVerifiedAt) });
    setNotice("");
  };
  const saveBasicInfo = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setNotice("");
    try {
      await apiFetch(`/admin/users/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          displayName: editForm.displayName.trim() || null,
          email: editForm.email,
          emailVerified: editForm.emailVerified,
        }),
      });
      setEditing(null);
      setNotice("用户基本信息已更新并写入审计日志。");
      await Promise.all([client.invalidateQueries({ queryKey: ["admin", "users"] }), client.invalidateQueries({ queryKey: ["admin", "overview"] })]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "request_failed";
      setNotice(message === "email_in_use" ? "该邮箱已被其他账号使用。" : "基本信息保存失败，请检查输入内容。");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-page">
      <PageHeader title="用户管理" description="编辑用户资料，调整访问状态和管理员权限。" />
      <section className="admin-toolbar" aria-label="用户筛选">
        <label className="admin-search"><Search /><Input aria-label="搜索用户" placeholder="邮箱或昵称" value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} /></label>
        <select className="input" aria-label="账号状态" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}><option value="">全部状态</option><option value="active">正常</option><option value="locked">已锁定</option><option value="disabled">已停用</option></select>
        <select className="input" aria-label="账号角色" value={role} onChange={(event) => { setRole(event.target.value); setPage(1); }}><option value="">全部角色</option><option value="ADMIN">管理员</option><option value="LEARNER">学习者</option></select>
        <select className="input" aria-label="邮箱验证状态" value={verified} onChange={(event) => { setVerified(event.target.value); setPage(1); }}><option value="">全部验证状态</option><option value="true">已验证</option><option value="false">未验证</option></select>
      </section>
      {notice ? <p className="admin-notice" role="status">{notice}</p> : null}
      <section className="admin-table-wrap">
        <div className="admin-data-table admin-users-table" role="table">
          <div className="admin-data-table__head" role="row"><span>用户</span><span>状态</span><span>角色</span><span>最近登录</span><span>操作</span></div>
          {query.isLoading ? <LoadingLine /> : null}
          {query.data?.users.map((user) => {
            const isSelf = user.id === query.data.currentUserId;
            const isAdmin = user.roles.includes("ADMIN");
            return (
              <div className="admin-data-table__row" role="row" key={user.id}>
                <span className="admin-user-cell"><span className="admin-user-avatar"><CircleUserRound /></span><span><strong>{user.displayName ?? "未设置昵称"}{isSelf ? "（当前）" : ""}</strong><small>{user.email}</small></span></span>
                <span><Tag tone={user.status === "active" ? "success" : user.status === "locked" ? "accent" : "neutral"}>{user.status === "active" ? "正常" : user.status === "locked" ? "已锁定" : "已停用"}</Tag><small>{user.emailVerifiedAt ? "邮箱已验证" : "邮箱未验证"}</small></span>
                <span className="tag-list">{user.roles.map((item) => <Tag key={item} tone={item === "ADMIN" ? "accent" : "neutral"}>{item === "ADMIN" ? "管理员" : "学习者"}</Tag>)}</span>
                <span>{formatDate(user.lastLoginAt)}</span>
                <span className="row-actions">
                  <button type="button" title="编辑基本信息" aria-label={`编辑 ${user.email} 的基本信息`} onClick={() => openEditor(user)}><Pencil /></button>
                  <select aria-label={`修改 ${user.email} 的状态`} value={user.status} disabled={isSelf} onChange={(event) => setPending({ user, body: { status: event.target.value as AdminUser["status"] }, title: "修改账号状态", description: `将 ${user.email} 设置为“${event.target.options[event.target.selectedIndex]?.text}”。非正常状态会立即撤销该用户的全部会话。` })}><option value="active">正常</option><option value="locked">锁定</option><option value="disabled">停用</option></select>
                  <button type="button" title={isAdmin ? "移除管理员" : "授予管理员"} aria-label={isAdmin ? "移除管理员" : "授予管理员"} disabled={isSelf && isAdmin} onClick={() => setPending({ user, body: { admin: !isAdmin }, title: isAdmin ? "移除管理员权限" : "授予管理员权限", description: `${user.email} ${isAdmin ? "将失去" : "将获得"}管理后台访问权限。` })}><ShieldCheck /></button>
                  <button type="button" title="撤销全部会话" aria-label="撤销全部会话" onClick={() => setPending({ user, revoke: true, title: "撤销全部会话", description: `${user.email} 将立即从所有设备退出登录。` })}><LockKeyhole /></button>
                </span>
              </div>
            );
          })}
        </div>
        {!query.isLoading && query.data?.users.length === 0 ? <EmptyState title="没有匹配用户" description="调整筛选条件后再试。" /> : null}
        {query.data ? <PaginationBar pagination={query.data.pagination} onPage={setPage} /> : null}
      </section>
      {editing ? (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !busy && setEditing(null)}>
          <section className="admin-confirm-dialog admin-edit-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-user-title" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="section-label">用户资料</p><h2 id="edit-user-title">编辑基本信息</h2></div><button type="button" className="icon-button" aria-label="关闭" onClick={() => setEditing(null)} disabled={busy}><X /></button></header>
            <form className="admin-edit-form" onSubmit={(event) => void saveBasicInfo(event)}>
              <Field label="昵称" htmlFor="admin-user-display-name" optional hint="留空将清除昵称。"><Input id="admin-user-display-name" maxLength={80} value={editForm.displayName} onChange={(event) => setEditForm({ ...editForm, displayName: event.target.value })} /></Field>
              <Field label="登录邮箱" htmlFor="admin-user-email" hint="修改邮箱后会撤销该用户其他设备上的会话。"><Input id="admin-user-email" type="email" required value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} /></Field>
              <label className="switch-row switch-row--compact"><input type="checkbox" checked={editForm.emailVerified} onChange={(event) => setEditForm({ ...editForm, emailVerified: event.target.checked })} /><span className="switch-control" aria-hidden="true"><span /></span><span><strong>邮箱已验证</strong><small>仅在确认该邮箱归属用户时启用。</small></span></label>
              <div className="admin-dialog-actions"><Button type="button" onClick={() => setEditing(null)} disabled={busy}>取消</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "正在保存…" : "保存基本信息"}</Button></div>
            </form>
          </section>
        </div>
      ) : null}
      {pending ? (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !busy && setPending(null)}>
          <section className="admin-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="admin-confirm-icon"><AlertTriangle /></div><h2 id="confirm-title">{pending.title}</h2><p>{pending.description}</p>
            <div className="admin-dialog-actions"><Button type="button" onClick={() => setPending(null)} disabled={busy}>取消</Button><Button variant="danger" type="button" onClick={() => void confirmAction()} disabled={busy}>{busy ? "处理中…" : "确认操作"}</Button></div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

interface ImportRow { id: string; kind: string; status: string; sourceName: string; authority: string; sourceSha256: string; createdAt: string; updatedAt: string }
interface ImportDetail extends ImportRow { sourceUrl: string | null; manifest: unknown; diffReport: unknown; errorMessage: string | null }
interface PoliticsAdminContent {
  banks: Array<{ id: string; code: string; name: string; edition: string | null; status: string; questionCount: number; updatedAt: string }>;
  imports: Array<{ id: string; bankId: string | null; status: string; parserVersion: string; statistics: unknown; issues: unknown; startedAt: string; completedAt: string | null }>;
}

export function AdminContentPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);
  const params = new URLSearchParams({ page: String(page), pageSize: "25" });
  if (q) params.set("q", q); if (status) params.set("status", status); if (kind) params.set("kind", kind);
  const query = useQuery({
    queryKey: ["admin", "content", q, status, kind, page],
    queryFn: () => apiFetch<{
      summary: { wordBooks: number; examPapers: number; politicsQuestionBanks: number; politicsQuestions: number; politicsImports: number };
      politics: PoliticsAdminContent;
      imports: ImportRow[];
      pagination: Pagination;
    }>(`/admin/content?${params}`),
  });
  const detail = useQuery({
    queryKey: ["admin", "content", "detail", detailId],
    queryFn: () => apiFetch<{ import: ImportDetail }>(`/admin/content/imports/${detailId}`),
    enabled: Boolean(detailId),
  });
  return (
    <div className="admin-page">
      <PageHeader title="内容与导入" description="查看已发布词库、真题和每次正式导入的来源证据。" />
      <section className="admin-summary-strip"><div><BookOpen /><span><strong>{query.data?.summary.wordBooks ?? "—"}</strong><small>已发布词库</small></span></div><div><FileSearch /><span><strong>{query.data?.summary.examPapers ?? "—"}</strong><small>已发布真题</small></span></div></section>
      {query.data?.politics.banks.length ? (
        <section className="admin-panel admin-politics-content" aria-labelledby="admin-politics-content-title">
          <header className="admin-panel__header">
            <div><p className="section-label">题库内容</p><h2 id="admin-politics-content-title">政治题库</h2></div>
            <Tag tone="accent">{query.data.summary.politicsQuestions} 道题</Tag>
          </header>
          <div className="admin-politics-content__list">
            {query.data.politics.banks.map((bank) => (
              <article key={bank.id}>
                <div><strong>{bank.name}</strong><small>{bank.edition ?? bank.code} · 更新于 {formatDate(bank.updatedAt)}</small></div>
                <span>{bank.questionCount} 题</span>
                <Tag tone={bank.status === "published" ? "success" : "neutral"}>{bank.status === "published" ? "已发布" : bank.status === "draft" ? "草稿" : bank.status}</Tag>
              </article>
            ))}
          </div>
          <footer>OCR / 导入审计 {query.data.summary.politicsImports} 次 · 题库内容只读展示</footer>
        </section>
      ) : null}
      <section className="admin-toolbar" aria-label="导入筛选">
        <label className="admin-search"><Search /><Input aria-label="搜索导入来源" placeholder="来源名称" value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} /></label>
        <select className="input" aria-label="导入类型" value={kind} onChange={(event) => { setKind(event.target.value); setPage(1); }}><option value="">全部类型</option><option value="vocabulary">词汇</option><option value="dictionary">词典</option><option value="exam_corpus">真题语料</option><option value="question_bank">政治题库</option></select>
        <select className="input" aria-label="导入状态" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}><option value="">全部状态</option><option value="completed">已完成</option><option value="review_required">待复核</option><option value="failed">失败</option><option value="processing">处理中</option><option value="queued">排队中</option></select>
      </section>
      <section className="admin-table-wrap">
        <div className="admin-data-table admin-imports-table" role="table">
          <div className="admin-data-table__head" role="row"><span>来源</span><span>类型</span><span>权威层级</span><span>状态</span><span>更新时间</span><span /></div>
          {query.isLoading ? <LoadingLine /> : null}
          {query.data?.imports.map((item) => <button className="admin-data-table__row admin-data-table__row--button" role="row" type="button" key={item.id} onClick={() => setDetailId(item.id)}><span><strong>{item.sourceName}</strong><small className="mono">{item.sourceSha256.slice(0, 14)}…</small></span><span>{item.kind}</span><span>{item.authority}</span><span><Tag tone={item.status === "completed" ? "success" : item.status === "review_required" ? "accent" : "neutral"}>{item.status}</Tag></span><span>{formatDate(item.updatedAt)}</span><ChevronRight /></button>)}
        </div>
        {!query.isLoading && query.data?.imports.length === 0 ? <EmptyState title="没有导入记录" description="当前筛选条件下没有可展示的记录。" /> : null}
        {query.data ? <PaginationBar pagination={query.data.pagination} onPage={setPage} /> : null}
      </section>
      {detailId ? <div className="admin-drawer-backdrop" role="presentation" onMouseDown={() => setDetailId(null)}><aside className="admin-drawer" role="dialog" aria-modal="true" aria-label="导入详情" onMouseDown={(event) => event.stopPropagation()}><header><div><p className="section-label">导入详情</p><h2>{detail.data?.import.sourceName ?? "正在读取"}</h2></div><button type="button" className="icon-button" aria-label="关闭" onClick={() => setDetailId(null)}><X /></button></header>{detail.isLoading ? <LoadingLine /> : null}{detail.data ? <div className="admin-detail-list"><div><span>状态</span><strong>{detail.data.import.status}</strong></div><div><span>类型 / 权威</span><strong>{detail.data.import.kind} · {detail.data.import.authority}</strong></div><div><span>SHA-256</span><code>{detail.data.import.sourceSha256}</code></div><div><span>创建 / 更新</span><strong>{formatDate(detail.data.import.createdAt)}<br />{formatDate(detail.data.import.updatedAt)}</strong></div>{detail.data.import.errorMessage ? <div className="admin-alert admin-alert--danger"><AlertTriangle />{detail.data.import.errorMessage}</div> : null}<section><h3>Manifest</h3><pre>{JSON.stringify(detail.data.import.manifest, null, 2)}</pre></section><section><h3>Diff report</h3><pre>{JSON.stringify(detail.data.import.diffReport, null, 2)}</pre></section></div> : null}</aside></div> : null}
    </div>
  );
}

interface AuditRow { id: number; actorEmail: string | null; action: string; targetType: string; targetId: string | null; metadata: unknown; createdAt: string }

export function AdminAuditPage() {
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [targetType, setTargetType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), pageSize: "25" });
  for (const [key, value] of Object.entries({ q, action, targetType, from, to })) if (value) params.set(key, value);
  const query = useQuery({
    queryKey: ["admin", "audit", q, action, targetType, from, to, page],
    queryFn: () => apiFetch<{ audit: AuditRow[]; pagination: Pagination }>(`/admin/audit?${params}`),
  });
  return (
    <div className="admin-page">
      <PageHeader title="审计日志" description="追踪管理员对账号、策略与运行配置的修改。" />
      <section className="admin-toolbar admin-toolbar--audit" aria-label="审计筛选">
        <label className="admin-search"><Search /><Input aria-label="搜索操作者或目标" placeholder="操作者邮箱或目标 ID" value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} /></label>
        <Input aria-label="动作" placeholder="动作，如 user.updated" value={action} onChange={(event) => { setAction(event.target.value); setPage(1); }} />
        <Input aria-label="目标类型" placeholder="目标类型" value={targetType} onChange={(event) => { setTargetType(event.target.value); setPage(1); }} />
        <Input type="date" aria-label="开始日期" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
        <Input type="date" aria-label="结束日期" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
      </section>
      <section className="audit-timeline">
        {query.isLoading ? <LoadingLine /> : null}
        {query.data?.audit.map((item) => <article key={item.id}><span className="audit-timeline__line"><span /></span><div className="audit-timeline__body"><header><div><strong>{item.action}</strong><Tag>{item.targetType}</Tag></div><time>{formatDate(item.createdAt)}</time></header><p>{item.actorEmail ?? "系统"}{item.targetId ? ` → ${item.targetId}` : ""}</p>{item.metadata && Object.keys(item.metadata as object).length ? <details><summary>查看变更信息</summary><pre>{JSON.stringify(item.metadata, null, 2)}</pre></details> : null}</div></article>)}
        {!query.isLoading && query.data?.audit.length === 0 ? <EmptyState title="没有审计记录" description="当前筛选条件下没有管理操作。" /> : null}
      </section>
      {query.data ? <PaginationBar pagination={query.data.pagination} onPage={setPage} /> : null}
    </div>
  );
}
